import type { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import React from 'react'
import { NextSeo } from 'next-seo'
import { groq } from 'next-sanity'
import { useRouter } from 'next/router'
import { seoImageLoader } from '@portfolio/components'
import {
  DEFAULT_REVALIDATE_TIME,
  ROUTE_BASE_ARTICLE,
  ROUTE_CATEGORY_ARTICLE,
} from '@portfolio/configs'
import { PageBuilderSection, PageResponse } from '@portfolio/models'
import { DefaultPage } from '@portfolio/page'
import {
  getArticleCategoryPage,
  getClient,
  getSiteConfiguration,
} from '@portfolio/services/sanity-service'

const InsightCategoryPage: NextPage<PageResponse<PageBuilderSection>> = (
  props,
) => {
  const { seo, disallowRobots, path } = props
  const { asPath } = useRouter()

  const canonicalPath = asPath?.split('?')[0] || path || ROUTE_BASE_ARTICLE
  const canonicalUrl = `${process.env.BASE_URL}${canonicalPath}`
  const seoImage = seoImageLoader(seo?.ogImage, seo?.ogAltImage)

  return (
    <>
      {seo ? (
        <NextSeo
          titleTemplate={seo?.titleTemplate}
          nofollow={disallowRobots}
          noindex={disallowRobots}
          title={seo.metaTitle}
          description={seo.metaDescription}
          canonical={canonicalUrl}
          openGraph={{
            url: canonicalUrl,
            type: 'website',
            title: seo.ogTitle,
            description: seo.ogDescription,
            images: [...(seoImage ? [seoImage] : [])],
          }}
          twitter={{
            cardType: 'summary_large_image',
          }}
        />
      ) : (
        <NextSeo
          noindex={disallowRobots}
          nofollow={disallowRobots}
          canonical={canonicalUrl}
        />
      )}
      <DefaultPage {...props} />
    </>
  )
}

export const getStaticPaths: GetStaticPaths = async ({ defaultLocale }) => {
  const categoryPaths: string[] = await getClient(false).fetch(
    groq`*[
      _type == 'articleTag' &&
      !(_id in path('drafts.**')) &&
      defined(path.current) &&
      count(
        *[
          _type == 'article' &&
          !(_id in path('drafts.**')) &&
          defined(path.current) &&
          references(^._id)
        ]
      ) > 0
    ]{
      "path": path.current
    }.path`,
  )

  const staticPaths = [...new Set(categoryPaths)]
    .map((path) => path.replace(`${ROUTE_CATEGORY_ARTICLE}/`, ''))
    .filter((category) => !!category && !category.includes('/'))
    .map((category) => ({
      params: { category },
      locale: defaultLocale,
    }))

  return {
    paths: staticPaths,
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps<
  PageResponse<PageBuilderSection>
> = async ({ params, locale, preview = false }) => {
  const { category } = params || {}

  if (!category || Array.isArray(category)) {
    return {
      notFound: true,
    }
  }

  // Due to PORT-210, we had to setup redirects like /Headless -> /headless,
  // but Next.js redirects are case-insensitive so we handle it here instead
  // Otherwise we would end up with a redirect loop.
  if (category !== category.toLocaleLowerCase()) {
    return {
      redirect: {
        destination: `${ROUTE_CATEGORY_ARTICLE}/${category.toLocaleLowerCase()}`,
        permanent: true,
      },
    }
  }

  const siteConfiguration = await getSiteConfiguration(locale, preview)
  const pageData = await getArticleCategoryPage({
    categorySlug: category,
    locale,
    preview,
  })

  if (!pageData) {
    return {
      redirect: {
        destination: ROUTE_BASE_ARTICLE,
        permanent: false,
      },
    }
  }

  return {
    props: {
      ...pageData,
      siteConfiguration,
    },
    revalidate: DEFAULT_REVALIDATE_TIME,
  }
}

export default InsightCategoryPage
